import { getAppConfig } from "@/config/app-config";
import { CipUser } from "@EspaceParkQC/sdk-services-legacy";
import axios from "axios";
import { FirebaseApp, getApps, initializeApp } from "firebase/app";
import {
  Auth,
  browserLocalPersistence,
  browserPopupRedirectResolver,
  getAuth,
  GoogleAuthProvider,
  initializeAuth,
  signInWithPopup,
  signOut,
} from "firebase/auth";

/*
  Staff session (the staff identity bridge)
  =========================================

  The call center signs staff in to the clicknpark-staff Firebase project, its own session in this
  browser. Its ID token goes to the clicknpark API through sdk-call-center, in the header the CIP
  token used, and the API tells them apart.
*/

const STAFF_APP_NAME = "staff";

let staffAuth: Auth | undefined;

function getStaffApp(): FirebaseApp {
  const existing = getApps().find((app) => app.name === STAFF_APP_NAME);
  if (existing) return existing;

  const config = getAppConfig();
  return initializeApp(
    {
      apiKey: config.VITE_STAFF_FIREBASE_API_KEY,
      authDomain: config.VITE_STAFF_FIREBASE_AUTH_DOMAIN,
      projectId: config.VITE_STAFF_FIREBASE_PROJECT_ID,
      appId: config.VITE_STAFF_FIREBASE_APP_ID,
    },
    STAFF_APP_NAME,
  );
}

function getStaffAuth(): Auth {
  if (staffAuth) return staffAuth;

  try {
    staffAuth = initializeAuth(getStaffApp(), {
      persistence: browserLocalPersistence,
      popupRedirectResolver: browserPopupRedirectResolver,
    });
  } catch {
    staffAuth = getAuth(getStaffApp());
  }

  return staffAuth;
}

/** Whether this page runs on a staff session Firebase restored. */
export async function resolveStaffSession(): Promise<boolean> {
  const auth = getStaffAuth();
  await auth.authStateReady();
  return Boolean(auth.currentUser);
}

/** Google sign-in on the staff project. Its blocking function refuses anyone without staff access. */
export async function signInStaffWithGoogle(): Promise<void> {
  await signInWithPopup(getStaffAuth(), new GoogleAuthProvider());
}

/** Signs out of the staff project. Awaited before leaving, or the saved session would sign right back in. */
export async function signOutOfStaff(): Promise<void> {
  await signOut(getStaffAuth());
}

/**
 * The signed-in staff member's ID token and when they last signed in, or undefined when nobody is.
 * Firebase hands back the token it holds and renews it first when it is about to expire (within 30
 * seconds; it lives an hour), so this is read before each request rather than kept.
 */
export async function getStaffIdToken(): Promise<{ token: string; signedInAt: Date } | undefined> {
  const user = getStaffAuth().currentUser;
  if (!user) return undefined;

  const { token, authTime } = await user.getIdTokenResult();
  return { token, signedInAt: new Date(authTime) };
}

/** `GET /staff/me` on the clicknpark API, in the shape the call center has always used for its user. */
export async function getStaffUser(token: string): Promise<CipUser> {
  const { data } = await axios.get<{
    uid: string;
    email: string;
    firstName: string;
    lastName: string;
    sipInfo: CipUser["sipInfo"] | null;
  }>(`${getAppConfig().VITE_CLICKNPARK_API_URL}/staff/me`, { headers: { "X-Staff-Auth": token } });

  return {
    id: data.uid,
    username: data.email,
    firstName: data.firstName,
    lastName: data.lastName,
    fullName: `${data.firstName} ${data.lastName}`.trim(),
    sipInfo: data.sipInfo ?? { identity: "", username: "", password: "", registrar: "" },
  } as CipUser;
}
